import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiCall } from "../lib/api";
import { 
  Users, Plus, Search, GraduationCap, TrendingUp, Calendar, 
  BookOpen, MessageSquare, Eye, Trash2, Link, Mail, Phone,
  CheckCircle, AlertCircle, DollarSign
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";

export const ParentPortal = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childDashboard, setChildDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ identifier: "", identifier_type: "email" });
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/portal?type=parent");
      return;
    }
    if (user.user_type !== "parent") {
      navigate("/dashboard");
      return;
    }
    fetchChildren();
  }, [user, navigate]);

  const fetchChildren = async () => {
    try {
      const data = await apiCall("GET", "/parent/children", null, token);
      setChildren(data || []);
      if (data && data.length > 0) {
        setSelectedChild(data[0]);
        fetchChildDashboard(data[0].child_id);
      }
    } catch (err) {
      console.error("Failed to fetch children:", err);
    }
    setLoading(false);
  };

  const fetchChildDashboard = async (childId) => {
    try {
      const data = await apiCall("GET", `/parent/child/${childId}/dashboard`, null, token);
      setChildDashboard(data);
    } catch (err) {
      console.error("Failed to fetch child dashboard:", err);
    }
  };

  const handleLinkChild = async () => {
    if (!linkForm.identifier) {
      toast.error("Please enter an email or phone number");
      return;
    }
    setLinking(true);
    try {
      const data = await apiCall("POST", "/parent/link-child", linkForm, token);
      toast.success(`Successfully linked ${data.child_name} to your account!`);
      setShowLinkDialog(false);
      setLinkForm({ identifier: "", identifier_type: "email" });
      fetchChildren();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link child");
    }
    setLinking(false);
  };

  const handleUnlinkChild = async (linkId, childName) => {
    if (!window.confirm(`Are you sure you want to unlink ${childName}?`)) return;
    try {
      await apiCall("DELETE", `/parent/unlink-child/${linkId}`, null, token);
      toast.success("Child unlinked successfully");
      fetchChildren();
      if (selectedChild?.id === linkId) {
        setSelectedChild(null);
        setChildDashboard(null);
      }
    } catch (err) {
      toast.error("Failed to unlink child");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-400 to-orange-400">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="parent-portal">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center">
              <Users className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#002147]">Parent Portal</h1>
              <p className="text-[#002147]/70">Welcome, {user?.first_name}! Monitor your children's progress</p>
            </div>
          </div>
        </div>

        {/* Children Overview */}
        <Card className="mb-8 bg-white">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap size={20} /> My Children
              </CardTitle>
              <CardDescription>Click on a child to view their details</CardDescription>
            </div>
            <Button 
              onClick={() => setShowLinkDialog(true)}
              className="bg-[#FF6B00] hover:bg-[#E65C00]"
              data-testid="link-child-btn"
            >
              <Plus size={16} className="mr-2" /> Link Child
            </Button>
          </CardHeader>
          <CardContent>
            {children.length === 0 ? (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-4">No children linked to your account yet</p>
                <Button onClick={() => setShowLinkDialog(true)} className="bg-[#002147]">
                  <Link size={16} className="mr-2" /> Link Your First Child
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {children.map((child) => (
                  <Card 
                    key={child.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      selectedChild?.id === child.id ? "ring-2 ring-[#FF6B00] bg-orange-50" : ""
                    }`}
                    onClick={() => {
                      setSelectedChild(child);
                      fetchChildDashboard(child.child_id);
                    }}
                    data-testid={`child-card-${child.child_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className="bg-[#002147] text-white text-lg">
                            {child.child_name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleUnlinkChild(child.id, child.child_name); }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <h3 className="font-semibold text-[#002147]">{child.child_name}</h3>
                      <p className="text-sm text-gray-500">{child.child_grade || "Grade N/A"}</p>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-green-50 p-2 rounded">
                          <span className="text-green-600 font-semibold">{child.gpa}</span>
                          <span className="text-gray-500 text-xs block">GPA</span>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <span className="text-blue-600 font-semibold">{child.attendance_rate}%</span>
                          <span className="text-gray-500 text-xs block">Attendance</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Child Dashboard */}
        {selectedChild && childDashboard && (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="grades">Grades</TabsTrigger>
              <TabsTrigger value="attendance">Attendance</TabsTrigger>
              <TabsTrigger value="classes">Classes</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Stats */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">{childDashboard.student.name}'s Performance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-500">GPA</span>
                        <span className="font-semibold">{childDashboard.gpa}/4.0</span>
                      </div>
                      <Progress value={(childDashboard.gpa / 4) * 100} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-500">Attendance</span>
                        <span className="font-semibold">{childDashboard.attendance_rate}%</span>
                      </div>
                      <Progress value={childDashboard.attendance_rate} className="h-2" />
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-500">Total Classes</p>
                      <p className="text-2xl font-bold text-[#002147]">{childDashboard.total_classes}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate("/messages")}
                    >
                      <MessageSquare size={18} className="mr-2" /> Message Teachers
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate(`/fees?student=${selectedChild.child_id}`)}
                    >
                      <DollarSign size={18} className="mr-2" /> Pay Fees
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate("/events")}
                    >
                      <Calendar size={18} className="mr-2" /> View Events
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate("/announcements")}
                    >
                      <BookOpen size={18} className="mr-2" /> Announcements
                    </Button>
                  </CardContent>
                </Card>

                {/* Assignments */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">Upcoming Assignments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px]">
                      {childDashboard.assignments?.length > 0 ? (
                        <div className="space-y-3">
                          {childDashboard.assignments.map((assignment, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg">
                              <p className="font-medium text-[#002147]">{assignment.title}</p>
                              <p className="text-sm text-gray-500">{assignment.subject}</p>
                              <Badge variant="outline" className="mt-1">Due: {assignment.due_date}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">No upcoming assignments</p>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="grades">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Recent Grades</CardTitle>
                </CardHeader>
                <CardContent>
                  {childDashboard.recent_grades?.length > 0 ? (
                    <div className="space-y-3">
                      {childDashboard.recent_grades.map((grade, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{grade.assignment_name}</p>
                            <p className="text-sm text-gray-500">{grade.subject}</p>
                          </div>
                          <Badge className={grade.grade >= 3 ? "bg-green-500" : grade.grade >= 2 ? "bg-yellow-500" : "bg-red-500"}>
                            {grade.grade.toFixed(1)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No grades recorded yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="attendance">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Attendance Records</CardTitle>
                </CardHeader>
                <CardContent>
                  {childDashboard.attendance_records?.length > 0 ? (
                    <div className="space-y-3">
                      {childDashboard.attendance_records.map((record, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {record.status === "present" ? (
                              <CheckCircle className="text-green-500" size={20} />
                            ) : (
                              <AlertCircle className="text-red-500" size={20} />
                            )}
                            <span>{record.date}</span>
                          </div>
                          <Badge variant={record.status === "present" ? "default" : "destructive"}>
                            {record.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No attendance records yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="classes">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Enrolled Classes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {childDashboard.classes?.map((cls, index) => (
                      <Card key={index} className="border-l-4 border-l-[#FF6B00]">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-[#002147]">{cls.name}</h4>
                          <p className="text-sm text-gray-500">{cls.teacher_name}</p>
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                            <Badge variant="outline">{cls.room}</Badge>
                            <span>{cls.schedule}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {(!childDashboard.classes || childDashboard.classes.length === 0) && (
                    <p className="text-gray-500 text-center py-8">Not enrolled in any classes yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Link Child Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link size={20} /> Link Your Child
            </DialogTitle>
            <DialogDescription>
              Enter your child's email or phone number to link their account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Search by</Label>
              <Select 
                value={linkForm.identifier_type} 
                onValueChange={(v) => setLinkForm({ ...linkForm, identifier_type: v })}
              >
                <SelectTrigger data-testid="link-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail size={16} /> Email Address
                    </div>
                  </SelectItem>
                  <SelectItem value="phone">
                    <div className="flex items-center gap-2">
                      <Phone size={16} /> Phone Number
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{linkForm.identifier_type === "email" ? "Email Address" : "Phone Number"}</Label>
              <Input
                type={linkForm.identifier_type === "email" ? "email" : "tel"}
                placeholder={linkForm.identifier_type === "email" ? "child@example.com" : "0551234567"}
                value={linkForm.identifier}
                onChange={(e) => setLinkForm({ ...linkForm, identifier: e.target.value })}
                data-testid="link-identifier-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#FF6B00] hover:bg-[#E65C00]"
              onClick={handleLinkChild}
              disabled={linking}
              data-testid="confirm-link-btn"
            >
              {linking ? "Linking..." : "Link Child"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentPortal;
